import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  FaRobot,
  FaCheckCircle,
  FaTimesCircle,
  FaPenSquare,
  FaArrowDown,
  FaArrowUp,
  FaSync,
  FaClock,
} from "react-icons/fa";
import { auth, db } from "../../firebase";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import AdminModal from "../../components/AdminModal";
import { useToast } from "../../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  pageContainer,
  pageTitle,
  pageSubtitle,
} from "../../utils/designTokens";

/**
 * SuperAdmin: revisor de respuestas del bot WhatsApp durante el modo learning.
 *
 * Durante las primeras 2 semanas tras prender el bot, las respuestas NO se
 * envian al residente — solo se loguean en bot_audit. Esta pantalla permite
 * revisar cada interaccion y marcarla como buena/mala/necesita-edicion. Eso
 * sirve para validar la calidad antes de pasar a auto_respond.
 *
 * MVP:
 *   - Lista interacciones recientes (limit 100).
 *   - Filtros: pendientes de revision / verdict / modo.
 *   - Click en una fila: detalle con mensaje del residente, respuesta del bot,
 *     contexto, y botones de verdict + notas.
 *   - KPIs: total, % buenas, tiempo promedio de respuesta.
 */

const VERDICTS = {
  good: { label: "Buena", icon: FaCheckCircle, color: colors.success, bg: colors.successLight },
  bad: { label: "Mala", icon: FaTimesCircle, color: colors.error, bg: colors.errorLight },
  needs_edit: { label: "Editar", icon: FaPenSquare, color: colors.warningDark, bg: colors.warningLight },
};

const RevisionBot = () => {
  const { showToast } = useToast();
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | good | bad | needs_edit | all
  const [selected, setSelected] = useState(null);
  const [reviewVerdict, setReviewVerdict] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [savingReview, setSavingReview] = useState(false);

  const fetchInteractions = useCallback(async () => {
    setLoading(true);
    try {
      // Firestore no permite combinar where + orderBy en distintos campos sin
      // indice compuesto, asi que traemos los ultimos 100 y filtramos en cliente.
      const q = query(
        collection(db, "bot_audit"),
        orderBy("timestamp", "desc"),
        limit(100)
      );
      const snap = await getDocs(q);
      setInteractions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("RevisionBot fetch error:", e);
      showToast("Error cargando interacciones del bot", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  const filtered = useMemo(() => {
    if (filter === "all") return interactions;
    if (filter === "pending") return interactions.filter((i) => !i.reviewVerdict);
    return interactions.filter((i) => i.reviewVerdict === filter);
  }, [interactions, filter]);

  // KPIs sobre todo el dataset (no solo el filtrado)
  const kpis = useMemo(() => {
    const total = interactions.length;
    const reviewed = interactions.filter((i) => i.reviewVerdict).length;
    const good = interactions.filter((i) => i.reviewVerdict === "good").length;
    const bad = interactions.filter((i) => i.reviewVerdict === "bad").length;
    const avgMs = total > 0
      ? Math.round(
          interactions.reduce((acc, i) => acc + (i.responseTimeMs || 0), 0) / total
        )
      : 0;
    const goodPct = reviewed > 0 ? Math.round((good / reviewed) * 100) : null;
    return { total, reviewed, good, bad, goodPct, avgMs };
  }, [interactions]);

  const openDetail = (interaction) => {
    setSelected(interaction);
    setReviewVerdict(interaction.reviewVerdict || null);
    setReviewNotes(interaction.reviewNotes || "");
  };

  const closeDetail = () => {
    if (savingReview) return;
    setSelected(null);
    setReviewVerdict(null);
    setReviewNotes("");
  };

  const saveReview = async () => {
    if (!selected || !reviewVerdict) {
      showToast("Elegi un verdict (Buena/Mala/Editar) primero", "error");
      return;
    }
    setSavingReview(true);
    try {
      const reviewer = auth.currentUser?.email || auth.currentUser?.uid || "unknown";
      await updateDoc(doc(db, "bot_audit", selected.id), {
        reviewVerdict,
        reviewNotes: reviewNotes.trim() || null,
        reviewedBy: reviewer,
        reviewedAt: serverTimestamp(),
      });
      showToast("Revision guardada", "success");
      closeDetail();
      await fetchInteractions();
    } catch (e) {
      console.error("saveReview error:", e);
      showToast(`Error: ${e.message}`, "error");
    } finally {
      setSavingReview(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncate = (s, n = 70) => {
    if (!s) return "—";
    return s.length > n ? s.slice(0, n) + "..." : s;
  };

  const columns = [
    {
      key: "timestamp",
      label: "Cuando",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>
          {formatTimestamp(val)}
        </span>
      ),
    },
    {
      key: "fromPhone",
      label: "Residente",
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
            {val || row.userEmail || "—"}
          </span>
          {row.buildingId && (
            <span style={{ fontSize: 11, color: colors.gray400 }}>
              Edif. {row.buildingId}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "incomingMessage",
      label: "Pregunta",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
          {truncate(val, 60)}
        </span>
      ),
    },
    {
      key: "botResponse",
      label: "Respuesta bot",
      render: (val, row) => (
        <span style={{
          fontSize: typography.sizes.sm,
          color: row.botSuccess ? colors.gray700 : colors.error,
          fontStyle: row.botSuccess ? "normal" : "italic",
        }}>
          {truncate(val, 80)}
        </span>
      ),
    },
    {
      key: "responseTimeMs",
      label: "Tiempo",
      align: "right",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>
          {val ? `${(val / 1000).toFixed(1)}s` : "—"}
        </span>
      ),
    },
    {
      key: "reviewVerdict",
      label: "Revision",
      align: "center",
      render: (val) => {
        if (!val) {
          return (
            <span style={{
              fontSize: typography.sizes.sm,
              padding: "2px 10px",
              background: colors.gray100,
              color: colors.gray600,
              borderRadius: radius.full,
            }}>
              Pendiente
            </span>
          );
        }
        const cfg = VERDICTS[val] || { label: val, icon: FaCheckCircle, color: colors.gray600, bg: colors.gray100 };
        const Icon = cfg.icon;
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "2px 10px",
            background: cfg.bg,
            color: cfg.color,
            borderRadius: radius.full,
            fontSize: typography.sizes.sm,
          }}>
            <Icon size={11} />
            {cfg.label}
          </span>
        );
      },
    },
  ];

  if (loading && interactions.length === 0) return <Loader />;

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitle}>Revisión del bot WhatsApp</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Cada respuesta del bot durante el modo "learning" se loguea acá para revisión humana.
            Calificá cada interacción para validar la calidad antes de habilitar la respuesta automática.
          </p>
        </div>
        <button onClick={fetchInteractions} style={ghostBtnStyle}>
          <FaSync size={11} />
          Actualizar
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: spacing[3], marginBottom: spacing[5], flexWrap: "wrap" }}>
        <KpiCard label="Total interacciones" value={kpis.total} />
        <KpiCard label="Revisadas" value={`${kpis.reviewed} / ${kpis.total}`} />
        <KpiCard
          label="% buenas (de revisadas)"
          value={kpis.goodPct === null ? "—" : `${kpis.goodPct}%`}
          color={kpis.goodPct === null ? colors.gray600 : (kpis.goodPct >= 70 ? colors.success : colors.warning)}
        />
        <KpiCard
          label="Tiempo promedio"
          value={kpis.avgMs > 0 ? `${(kpis.avgMs / 1000).toFixed(1)}s` : "—"}
          icon={FaClock}
        />
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" }}>
        {[
          { id: "pending", label: "Pendientes" },
          { id: "good", label: "Buenas" },
          { id: "bad", label: "Malas" },
          { id: "needs_edit", label: "Necesitan editar" },
          { id: "all", label: "Todas" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: "8px 16px",
              fontSize: typography.sizes.base,
              fontWeight: 400,
              background: filter === f.id ? colors.primary : colors.white,
              color: filter === f.id ? colors.white : colors.gray700,
              border: `1px solid ${filter === f.id ? colors.primary : colors.gray300}`,
              borderRadius: radius.sm,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={FaRobot}
          title={filter === "pending" ? "No hay interacciones pendientes de revisar" : "No hay interacciones en este filtro"}
          description={
            interactions.length === 0
              ? "Cuando el bot procese mensajes en modo learning aparecerán acá. Si la WABA todavía está restringida, se va a poblar cuando se levante."
              : "Probá con otro filtro o esperá nuevas interacciones."
          }
        />
      ) : (
        <DataTable columns={columns} data={filtered} onRowClick={openDetail} />
      )}

      <AdminModal
        open={!!selected}
        onClose={closeDetail}
        title="Interacción del bot"
        size="wide"
        icon={<FaRobot size={20} color={colors.primary} />}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button onClick={closeDetail} disabled={savingReview} style={ghostBtnStyle}>
              Cancelar
            </button>
            <button onClick={saveReview} disabled={savingReview || !reviewVerdict} style={primaryBtnStyle}>
              {savingReview ? "Guardando..." : "Guardar revisión"}
            </button>
          </div>
        }
      >
        {selected && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            {/* Mensaje del residente */}
            <div>
              <div style={smallLabel}>
                <FaArrowDown size={10} /> Mensaje del residente
              </div>
              <div style={{ ...messageBubbleStyle, background: colors.gray100 }}>
                {selected.incomingMessage || "—"}
              </div>
              <div style={metaRow}>
                <span>De: {selected.fromPhone || "—"}</span>
                <span>{formatTimestamp(selected.timestamp)}</span>
              </div>
            </div>

            {/* Respuesta del bot */}
            <div>
              <div style={smallLabel}>
                <FaArrowUp size={10} /> Respuesta del bot
              </div>
              <div style={{
                ...messageBubbleStyle,
                background: selected.botSuccess ? "#DCF8C6" : colors.errorLight,
                color: selected.botSuccess ? colors.gray900 : colors.errorDark,
              }}>
                {selected.botResponse || (selected.botError ? `Error: ${selected.botError}` : "—")}
              </div>
              <div style={metaRow}>
                <span>{selected.botSuccess ? "OK" : "Falló"}</span>
                <span>
                  {selected.responseTimeMs ? `${(selected.responseTimeMs / 1000).toFixed(2)}s` : "—"}
                </span>
                <span>
                  Modo: <strong>{selected.mode || "learning"}</strong>
                </span>
                <span>
                  {selected.responseSent ? "✓ enviada al residente" : "no enviada (solo log)"}
                </span>
              </div>
            </div>

            {/* Contexto */}
            <div style={{
              padding: spacing[3],
              background: colors.gray50,
              borderRadius: radius.sm,
              fontSize: typography.sizes.sm,
              color: colors.gray700,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 12px",
            }}>
              <span style={{ color: colors.gray600 }}>Usuario:</span>
              <span>{selected.userEmail || `userid ${selected.userId}` || "—"}</span>
              <span style={{ color: colors.gray600 }}>Edificio:</span>
              <span>{selected.buildingId || "—"}</span>
              {selected.incomingMetaMessageId && (
                <>
                  <span style={{ color: colors.gray600 }}>Meta msg:</span>
                  <code style={{ fontSize: 11 }}>{selected.incomingMetaMessageId}</code>
                </>
              )}
              {selected.reviewedBy && (
                <>
                  <span style={{ color: colors.gray600 }}>Revisado por:</span>
                  <span>{selected.reviewedBy} · {formatTimestamp(selected.reviewedAt)}</span>
                </>
              )}
            </div>

            {/* Verdict */}
            <div>
              <div style={{ ...smallLabel, marginBottom: spacing[2] }}>Tu evaluación</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(VERDICTS).map(([id, cfg]) => {
                  const Icon = cfg.icon;
                  const active = reviewVerdict === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setReviewVerdict(id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        fontSize: typography.sizes.base,
                        fontWeight: 400,
                        background: active ? cfg.color : colors.white,
                        color: active ? colors.white : cfg.color,
                        border: `1px solid ${cfg.color}`,
                        borderRadius: radius.sm,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <Icon size={12} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notas */}
            <div>
              <div style={{ ...smallLabel, marginBottom: spacing[1] }}>Notas (opcional)</div>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder='Ej: "El bot inventó una fecha", "Le faltó preguntar el número de unidad", "Respuesta correcta y precisa"'
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: typography.sizes.base,
                  fontWeight: 400,
                  color: colors.gray900,
                  background: colors.white,
                  border: `1px solid ${colors.gray300}`,
                  borderRadius: radius.sm,
                  outline: "none",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

const KpiCard = ({ label, value, color, icon: Icon }) => (
  <div style={{
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    minWidth: 180,
    flex: "1 1 180px",
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  }}>
    {Icon && (
      <div style={{
        width: 36, height: 36,
        borderRadius: radius.sm,
        background: colors.primaryLighter,
        color: colors.primary,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={14} />
      </div>
    )}
    <div>
      <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{label}</div>
      <div style={{
        fontSize: typography.sizes.lg,
        fontWeight: 400,
        color: color || colors.gray900,
      }}>
        {value}
      </div>
    </div>
  </div>
);

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: spacing[4],
  gap: spacing[4],
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray700,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.white,
  background: colors.primary,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const smallLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: typography.sizes.sm,
  color: colors.gray600,
  marginBottom: 4,
};

const messageBubbleStyle = {
  padding: spacing[3],
  borderRadius: radius.md,
  fontSize: typography.sizes.base,
  lineHeight: 1.5,
  color: colors.gray900,
  whiteSpace: "pre-wrap",
};

const metaRow = {
  display: "flex",
  gap: spacing[3],
  marginTop: 4,
  fontSize: 11,
  color: colors.gray400,
  flexWrap: "wrap",
};

export default RevisionBot;
